import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import type { DocumentFindInterface } from '@zaoblako/shared/modules/document/dtos/document/document-find.dto';
import { ChartModeEnum } from '@zaoblako/shared/modules/dashboard/enums/chart-mode.enum';
import type { DocumentQueryInterface } from '@zaoblako/shared/modules/document/dtos/document/document-query.dto';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { DocumentRegistryService } from '@zaoblako/web/core/services/document/document-registry.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { QueryParamsService } from '@zaoblako/web/core/services/query-params.service';
import { ButtonShowAndHideComponent } from '@zaoblako/web/shared/components/button-show-and-hide/button-show-and-hide.component';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { SumPipe } from '@zaoblako/web/shared/pipes/sum.pipe';
import moment from 'moment-timezone';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-document-split-widget',
  templateUrl: './document-split-widget.component.html',
  imports: [
    CommonModule,
    CardModule,
    ButtonShowAndHideComponent,
    SumPipe,
    SpinnerComponent,
    TableComponent,
    ButtonComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentSplitWidgetComponent implements OnInit, OnDestroy {
  @Input() public readonly index: number;

  public documentsInfo: {
    documents: DocumentFindInterface[];
    loading: boolean;
    documentsShow: boolean;
  } = {
    documents: [],
    loading: true,
    documentsShow: true
  };

  public columns: CrmTableColumn[] = [{ label: '№' }, { label: 'Сумма' }];

  private formSubscription$: Subscription = new Subscription();
  private querySubscription$: Subscription;

  public constructor(
    private readonly documentRegistryService: DocumentRegistryService,
    private readonly formStoreService: FormStoreService,
    private readonly queryParamsService: QueryParamsService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    if (this.documentsInfo.documentsShow) {
      this.loadDocuments();
    }
  }

  public ngOnDestroy(): void {
    this.formSubscription$?.unsubscribe();
    this.querySubscription$?.unsubscribe();
  }

  public loadDocuments() {
    this.documentsInfo = {
      ...this.documentsInfo,
      loading: true
    };

    this.documentRegistryService
      .getRelatedDocuments(this.formStoreService.getState.forms[this.index]?.entityId as any)
      .subscribe({
        next: (documents) => {
          this.documentsInfo = {
            ...this.documentsInfo,
            documents,
            loading: false
          };
          this.cdRef.markForCheck();
        }
      });
  }

  public toDocument(document: any): DocumentFindInterface {
    return document as DocumentFindInterface;
  }

  public showButtonChange(isShow: boolean) {
    if (isShow) {
      this.loadDocuments();
    }
    this.documentsInfo = {
      ...this.documentsInfo,
      documentsShow: !this.documentsInfo.documentsShow
    };
  }

  public editDocument(code: string) {
    const result = this.formStoreService.editForm(TypeFormEnum.DOCUMENT, code as any, {
      headerText: `Редактирование документа №${code}`,
      isShowShare: true
    });

    this.formSubscription$.add(result.onSave.subscribe(() => this.loadDocuments()));
    this.formSubscription$.add(result.onDelete.subscribe(() => this.loadDocuments()));
  }

  public directToDocumentsList() {
    const queryParams: DocumentQueryInterface = {
      from: moment().subtract(1, 'month').format('YYYY-MM-DD') as any as Date,
      to: moment().format('YYYY-MM-DD') as any as Date,
      periodChart: ChartModeEnum.WEEK
    };
    this.querySubscription$ = this.queryParamsService
      .getUrlTreeWithDigest(['/document/list'], queryParams, true)
      .subscribe({
        next: (urlTree) => {
          const url = String(urlTree);
          window.open(url, '_blank');
        },
        error: (err) => {
          console.error('Failed to generate URL with digest:', err);
          this.errorService.addCustomError('Ошибка', 'Произошла ошибка, вернитесь на главную и попробуйте снова.');
        }
      });
  }
}
